import { useAsync, useAsyncFn } from 'react-use-v13';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { PerAnnotationError } from '@scale/shared/types/customerAudit';
import { AuditGradeV2, AuditResultV2 } from '@scale/shared/defaults';
import { useEffect } from 'react';
import { SpeedAuditLookup } from '../../types';

const useSpeedAuditFeedback = ({
  attemptId,
  attemptAuditId,
  onLoadPreviousFeedback,
  onAuditUpdate,
  objectId,
}: {
  attemptAuditId: string;
  objectId: string;
  attemptId: string;
  onLoadPreviousFeedback?: (prev: AuditGradeV2) => void;
  onAuditUpdate?: (audit: SpeedAuditLookup) => void;
}) => {
  // 1. Get default values
  const { loading, error, value } = useAsync<{
    grade: AuditGradeV2 | null;
    time: number | null;
  }>(() => {
    return apiFetch<{
      grade: AuditGradeV2 | null;
      time: number | null;
    }>(null, `corp-api/attemptAudit/previousFeedback`, {
      query: {
        objectId,
        attemptId,
      },
    });
  });

  useEffect(() => {
    if (value && value.grade && onLoadPreviousFeedback) {
      onLoadPreviousFeedback(value.grade);
    }
  }, [loading, error, value, onLoadPreviousFeedback]);

  // 2. Save changes
  const [, setAuditValue] = useAsyncFn(
    async (newValue?: {
      resultV2: {
        attemptId: string;
        errorCategories: string[];
        result: AuditResultV2;
        feedback: string;
      };
      flagResult?: string;
      comments?: string;
      taskInstructionItems?: string[];
      perAnnotationInstructionItems?: PerAnnotationError[];
    }) => {
      if (!newValue) return;

      const {
        resultV2,
        flagResult,
        comments,
        taskInstructionItems,
        perAnnotationInstructionItems,
      } = newValue;

      const updateResult = await apiFetch(
        null,
        `corp-api/attemptAudit/attemptAudits/${attemptAuditId}`,
        {
          method: 'POST',
          body: {
            resultV2,
            flagResult,
            comments,
            taskInstructionItems,
            perAnnotationInstructionItems,
          },
        },
      );
      if (onAuditUpdate) onAuditUpdate(updateResult.attemptAudit);

      return updateResult;
    },
  );

  return {
    updateSpeedAuditFeedback: setAuditValue,
    previousAuditGrade: value,
    previousAuditGradeLoading: loading,
    previousAuditGradeError: error,
  };
};

export default useSpeedAuditFeedback;
